import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, upload } from "../api/client";
import type { Project } from "../api/types";
import { Icon } from "../components/icons";
import { toast } from "../components/toast";
import { formatBytes } from "../lib/format";

export function NewProject() {
  const navigate = useNavigate();
  // Set when this flow was launched from the Workflows board ("Add projects →
  // Create new"): create the project the normal way, then link + track it on the
  // board and return there.
  const track = !!(useLocation().state as { trackInWorkflows?: boolean } | null)?.trackInWorkflows;
  const [name, setName] = useState("");
  const [pdf, setPdf] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string>("");
  const pdfInput = useRef<HTMLInputElement>(null);
  const [dragPdf, setDragPdf] = useState(false);

  const canCreate = name.trim() && !busy;

  async function create() {
    if (!canCreate) return;
    setBusy(true);
    try {
      setProgress("Creating project…");
      const project = await api<Project>("/api/projects", {
        method: "POST",
        body: JSON.stringify({ name: name.trim() }),
      });

      // Manuscript is optional — only proofing needs it. Skip it and you land in
      // the hub where you can drop audio and run a spec check, or add the PDF later.
      if (pdf) {
        const pdfForm = new FormData();
        pdfForm.append("file", pdf);
        await upload(`/api/projects/${project.id}/manuscript`, pdfForm, (pct) =>
          setProgress(`Uploading manuscript… ${pct}%`)
        );
      }

      if (track) {
        // Best-effort link — the project itself is created regardless.
        try {
          await api("/api/workflows", {
            method: "POST",
            body: JSON.stringify({ project_id: project.id, name: project.name, status: "announced" }),
          });
        } catch { /* leave it unlinked; the user can pull it in later */ }
        toast(`“${project.name}” created and added to Workflows.`, "success");
        navigate("/workflows");
        return;
      }
      toast(`“${project.name}” created — add your files next.`, "success");
      navigate(`/projects/${project.id}`);
    } catch (e: any) {
      toast(e.message || "That didn't go through — mind trying again?", "error");
      setBusy(false);
      setProgress("");
    }
  }

  return (
    <div className="page">
      <div className="breadcrumb">
        <Link to={track ? "/workflows" : "/"}>{track ? "Workflows" : "Projects"}</Link> <Icon name="chevron-right" size={13} /> New
      </div>
      <div className="page-head">
        <h1>New project</h1>
      </div>

      <div className="card form-card">
        <div className="field">
          <span className="label">Project name</span>
          <input
            className="input"
            placeholder="e.g. The Layover — Round 1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); create(); } }}
            autoFocus
          />
        </div>

        <div className="field">
          <span className="label">Manuscript <em className="muted">(PDF or Word · optional)</em></span>
          <div
            className={`dropzone ${dragPdf ? "drag" : ""}`}
            onClick={() => pdfInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragPdf(true); }}
            onDragLeave={() => setDragPdf(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragPdf(false);
              const f = Array.from(e.dataTransfer.files).find((x) => /\.(pdf|docx|doc)$/i.test(x.name));
              if (f) setPdf(f);
              else toast("That isn't a PDF or Word file", "error");
            }}
          >
            <input
              ref={pdfInput}
              type="file"
              accept="application/pdf,.pdf,.docx,.doc"
              hidden
              onChange={(e) => e.target.files?.[0] && setPdf(e.target.files[0])}
            />
            {pdf ? (
              <div className="row" style={{ justifyContent: "center", gap: 10 }}>
                <Icon name="document" size={18} />
                <span className="mono">{pdf.name}</span>
                <span className="muted">({formatBytes(pdf.size)})</span>
              </div>
            ) : (
              <div className="hint">Drop the manuscript (PDF or Word) here, or click to choose</div>
            )}
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            Add a manuscript to proof the narration against it — or skip it and just run
            a technical spec check on your audio. You can add audio (and a manuscript)
            on the next screen any time.
          </div>
        </div>

        {busy && (
          <div className="row" style={{ gap: 10, marginBottom: 14 }}>
            <span className="spinner" />
            <span className="muted">{progress}</span>
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}>
          <Link to={track ? "/workflows" : "/"} className="btn">Cancel</Link>
          <button className="btn primary" disabled={!canCreate} onClick={create}>
            Create project
          </button>
        </div>
      </div>
    </div>
  );
}
